import React, { useRef, useContext, useEffect, useState } from 'react';
import { ChatContext } from './ChatContext';
import { UserContext } from './App';
import { FaTimesCircle, FaTimes, FaShieldAlt, FaCommentDots, FaCircle, FaVolumeUp, FaInfoCircle, FaCoins, FaClock, FaBars } from 'react-icons/fa';
import { ChatMessageList, ChatInputBar } from './ChatPanel';
import TipModal from './TipModal';
import { motion, AnimatePresence } from 'framer-motion';

/* ── Premium Rules Modal ── */
const RulesModal = ({ onClose }) => (
  <div style={{
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 10000,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    backdropFilter: 'blur(8px)',
  }} onClick={onClose}>
    <motion.div
      initial={{ opacity: 0, scale: 0.95, y: 20 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95, y: 20 }}
      onClick={e => e.stopPropagation()}
      style={{
        background: '#1a1c22',
        border: '1px solid #2d3748',
        borderRadius: '16px',
        width: '90%',
        maxWidth: '420px',
        color: '#fff',
        boxShadow: '0 20px 50px rgba(0,0,0,0.5)',
        overflow: 'hidden',
        position: 'relative'
      }}
    >
      <div style={{ padding: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 style={{ margin: 0, fontSize: '22px', fontWeight: 900 }}>Chat Rules</h2>
        <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: '#9ca3af', cursor: 'pointer', padding: '4px' }}>
          <FaTimes size={20} />
        </button>
      </div>

      <div style={{ padding: '0 24px 24px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
        {[
          'No spamming, no posting links',
          'No begging, no asking for loans',
          'No racism, harassment or hate speech',
          'Do not slander RustyCoin in any way',
          'Do not advertise your affiliate code',
          'Do not mention or promote other sites',
          'Do not ask for other people\'s socials or info in chat',
          'Only English is allowed'
        ].map((rule, i) => (
          <div key={i} style={{ display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
            <div style={{ width: '5px', height: '5px', borderRadius: '50%', background: '#fff', marginTop: '8px', flexShrink: 0 }} />
            <span style={{ fontSize: '15px', color: '#fff', fontWeight: 800, lineHeight: '1.4' }}>{rule}</span>
          </div>
        ))}

        <div style={{ marginTop: '12px' }}>
          <p style={{ margin: 0, fontSize: '14px', color: '#9ca3af', fontWeight: 800, lineHeight: '1.6' }}>
            By using our chat, you agree to follow these rules. Failure to comply may result in a mute or ban.
          </p>
        </div>

        <button 
          onClick={onClose}
          style={{
            marginTop: '16px',
            background: '#10b981',
            color: '#000',
            border: 'none',
            borderRadius: '8px',
            padding: '16px',
            fontSize: '16px',
            fontWeight: 900,
            cursor: 'pointer',
            transition: 'all 0.2s',
            boxShadow: '0 4px 15px rgba(16, 185, 129, 0.3)'
          }}
        >
          Agree
        </button>
      </div>
      
      {/* Bottom accent border */}
      <div style={{ height: '3px', background: 'linear-gradient(90deg, #ffb703, #ffb703)', width: '100%' }} />
    </motion.div>
  </div>
);


/* ── Main ChatSidebar ── */
const ChatSidebar = ({ mutedUsers, setMutedUsers }) => {
  const { chatMessages, sendMessage, onlineCount } = useContext(ChatContext);
  const { user } = useContext(UserContext);
  const [chatInput, setChatInput] = useState('');
  const [sending, setSending] = useState(false);
  const [muteError, setMuteError] = useState(null);
  const [replyTo, setReplyTo] = useState(null);
  const [isTipModalOpen, setIsTipModalOpen] = useState(false);
  const [showRules, setShowRules] = useState(false);

  const chatEndRef = useRef(null);
  const lastSentRef = useRef(0);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'auto' });
  }, [chatMessages]);

  const handleSendMessage = () => {
    if (!chatInput.trim()) return;

    // Handle /tip command to open modal
    const lowerInput = chatInput.trim().toLowerCase();
    if (lowerInput === '/tip' || lowerInput.startsWith('/tip ')) {
      setIsTipModalOpen(true);
      setChatInput(''); // Clear input after opening modal
      return;
    }
    
    // Cooldown check (1.5 seconds)
    const now = Date.now();
    if (now - lastSentRef.current < 1500) {
      setMuteError('Slow down! Anti-spam cooldown active (1.5s).');
      setTimeout(() => setMuteError(null), 3000);
      return;
    }

    if (user?.isChatMuted) {
      setMuteError(`You are muted: ${user.muteReason || 'No reason provided'}`);
      setChatInput('');
      setTimeout(() => setMuteError(null), 5000);
      return;
    }

    setSending(true);
    lastSentRef.current = now;
    sendMessage(chatInput.trim(), replyTo);
    setChatInput('');
    setReplyTo(null);
    setSending(false);
  };

  return (
    <aside className="rc-sidebarRight" style={{ display: 'flex', flexDirection: 'column', height: '100%', background: 'var(--card-dark, #0c0c14)', borderLeft: '1px solid var(--border, #1c1c32)' }}>
      {/* Modals */}
      <AnimatePresence>
        {showRules && <RulesModal onClose={() => setShowRules(false)} />}
      </AnimatePresence>
      {isTipModalOpen && (
        <TipModal 
          onClose={() => setIsTipModalOpen(false)} 
          initialRecipient={chatInput.split(' ')[1] || ''} 
        />
      )}
      {/* ── Chat Header ── */}
      <div style={{
        padding: '0 16px',
        background: '#1a1c22',
        borderBottom: '1px solid #2d3748',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        height: '60px',
        flexShrink: 0
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <FaCommentDots color="#ffb703" size={16} />
          <span style={{ color: '#fff', fontSize: '15px', fontWeight: 900, letterSpacing: '0.5px' }}>CHAT</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <FaVolumeUp color="#6b7280" size={14} style={{ cursor: 'pointer' }} />
        </div>
      </div>

      {/* ── Messages ── */}
      <ChatMessageList
        chatMessages={chatMessages.slice(-18)}
        mutedUsers={mutedUsers}
        user={user}
        chatEndRef={chatEndRef}
        onReply={(msg) => setReplyTo(msg)}
      />

      {/* ── Mute Error Banner ── */}
      <AnimatePresence>
        {muteError && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            style={{
              background: 'rgba(255, 59, 48, 0.1)',
              color: 'var(--danger, #ff3b30)',
              padding: '8px 12px',
              margin: '0 10px 6px',
              borderRadius: 'var(--button-radius, 8px)',
              fontSize: '12px',
              fontWeight: '600',
              border: '1px solid rgba(255, 59, 48, 0.2)',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              overflow: 'hidden',
            }}
          >
            <FaTimesCircle style={{ flexShrink: 0 }} size={14} /> {muteError}
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Input & Bottom Bar ── */}
      <div style={{ background: '#1a1c22', borderTop: '1px solid #2d3748', padding: '12px' }}>
        <ChatInputBar
          chatInput={chatInput}
          setChatInput={setChatInput}
          onSubmit={handleSendMessage}
          sending={sending}
          user={user}
          replyTo={replyTo}
          onCancelReply={() => setReplyTo(null)}
          onRulesClick={() => setShowRules(true)}
        />
        
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px' }}>
          <div style={{ background: 'rgba(16, 185, 129, 0.1)', border: '1px solid rgba(16, 185, 129, 0.2)', borderRadius: '6px', padding: '4px 10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <FaCircle size={6} color="#10b981" />
            <span style={{ color: '#10b981', fontSize: '11px', fontWeight: 900 }}>{typeof onlineCount === 'number' ? onlineCount.toLocaleString() : '—'} Online</span>
          </div>
          <button 
            onClick={() => setShowRules(true)}
            style={{ background: '#111318', border: '1px solid #2d3748', color: '#6b7280', width: '28px', height: '28px', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
            <FaBars size={12} />
          </button>
        </div>
      </div>
    </aside>
  );
};

export default ChatSidebar;
